/**
 * mSTEM UC-43 "Quản lý thiết bị mở rộng" manager container (MOCK-ONLY).
 * Lists user-declared devices from mstem.customDevices in an MstemTable and
 * wires the three management transactions:
 *   - S-1 search: filters the list by name (local state, ExtManagerModal).
 *   - S-2 Sửa: stash the row as the edit-entry (setExtDeviceEditEntry), close
 *     this manager, open the EXT-01 wizard prefilled (update-by-id on save).
 *   - S-3 Xóa: window.confirm → removeCustomDevice (localStorage) → refresh the
 *     device library card list → extDeviceDeleted toast → live re-render.
 *
 * Reads the live list straight from localStorage and mirrors it to local state
 * so a delete re-renders immediately without a Redux slice (the declared list
 * is not otherwise in Redux). React 16 class component (no hooks).
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import {
    closeExtDeviceManager,
    openDeviceDeclaration,
    setExtDeviceEditEntry
} from '../reducers/modals';
import {setDeviceData} from '../reducers/device-data';
import {showAlertWithTimeout} from '../reducers/alerts';
import {readCustomDevices, removeCustomDevice} from '../lib/mstem-extension-storage.js';
import {makeDeviceLibrary} from '../lib/libraries/devices/index.jsx';

import ExtManagerModalComponent from '../components/ext-manager-modal/ext-manager-modal.jsx';

const messages = defineMessages({
    title: {
        defaultMessage: 'Quản lý thiết bị mở rộng',
        description: 'UC-43 device manager modal title',
        id: 'gen.ext.manage.device.title'
    },
    subtitle: {
        defaultMessage: 'Các thiết bị bạn đã khai báo (mô phỏng — lưu trên trình duyệt).',
        description: 'UC-43 device manager subtitle',
        id: 'gen.ext.manage.device.subtitle'
    },
    colName: {
        defaultMessage: 'Tên',
        description: 'UC-43 device table column — name',
        id: 'gen.ext.manage.device.colName'
    },
    colType: {
        defaultMessage: 'Loại',
        description: 'UC-43 device table column — type',
        id: 'gen.ext.manage.device.colType'
    },
    colProtocol: {
        defaultMessage: 'Giao thức',
        description: 'UC-43 device table column — protocol',
        id: 'gen.ext.manage.device.colProtocol'
    },
    empty: {
        defaultMessage: 'Chưa có thiết bị nào được khai báo.',
        description: 'UC-43 device manager empty state',
        id: 'gen.ext.manage.device.empty'
    },
    confirmDelete: {
        defaultMessage: 'Xóa thiết bị này khỏi danh sách?',
        description: 'UC-43 S-3 delete confirm',
        id: 'gen.ext.manage.device.confirmDelete'
    },
    typeRobot: {
        defaultMessage: 'Robot',
        description: 'UC-43 device type — robot',
        id: 'gen.ext.manage.device.type.robot'
    },
    typeKit: {
        defaultMessage: 'Kit SmartHome',
        description: 'UC-43 device type — kit',
        id: 'gen.ext.manage.device.type.kit'
    },
    typeOther: {
        defaultMessage: 'Khác',
        description: 'UC-43 device type — other',
        id: 'gen.ext.manage.device.type.other'
    },
    none: {
        defaultMessage: '(không)',
        description: 'UC-43 device table — empty value',
        id: 'gen.ext.manage.device.none'
    }
});

class ExtDeviceManagerModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickEdit',
            'handleClickDelete',
            'handleSearchChange',
            'handleRequestClose',
            'renderTypeCell'
        ]);
        this.state = {
            devices: readCustomDevices(),
            search: ''
        };
    }
    refreshList () {
        this.setState({devices: readCustomDevices()});
    }
    renderTypeCell (row) {
        const {intl} = this.props;
        if (row.type === 'robot') return intl.formatMessage(messages.typeRobot);
        if (row.type === 'kit') return intl.formatMessage(messages.typeKit);
        if (row.type === 'other') return intl.formatMessage(messages.typeOther);
        return intl.formatMessage(messages.none);
    }
    buildColumns () {
        const {intl} = this.props;
        return [
            {key: 'name', label: intl.formatMessage(messages.colName)},
            {key: 'type', label: intl.formatMessage(messages.colType), render: this.renderTypeCell},
            {key: 'protocol', label: intl.formatMessage(messages.colProtocol), width: '160px'}
        ];
    }
    handleClickEdit (row) {
        // Stash the entry, close the manager, open the EXT-01 wizard prefilled.
        this.props.onSetEditEntry(row);
        this.props.onClose();
        this.props.onOpenWizard();
    }
    handleClickDelete (row) {
        const msg = this.props.intl.formatMessage(messages.confirmDelete);
        // eslint-disable-next-line no-alert
        if (typeof window !== 'undefined' && window.confirm && !window.confirm(msg)) {
            return;
        }
        removeCustomDevice(row.id);
        this.refreshList();
        // Refresh the device library so the deleted card disappears too.
        this.props.onSetDeviceData(makeDeviceLibrary());
        showAlertWithTimeout(this.props.dispatch, 'extDeviceDeleted');
    }
    handleSearchChange (value) {
        this.setState({search: value});
    }
    handleRequestClose () {
        this.props.onClose();
    }
    render () {
        const {intl} = this.props;
        return (
            <ExtManagerModalComponent
                columns={this.buildColumns()}
                emptyMessage={intl.formatMessage(messages.empty)}
                rows={this.state.devices}
                searchValue={this.state.search}
                subtitle={intl.formatMessage(messages.subtitle)}
                title={intl.formatMessage(messages.title)}
                onClickDelete={this.handleClickDelete}
                onClickEdit={this.handleClickEdit}
                onRequestClose={this.handleRequestClose}
                onSearchChange={this.handleSearchChange}
            />
        );
    }
}

ExtDeviceManagerModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpenWizard: PropTypes.func.isRequired,
    onSetDeviceData: PropTypes.func.isRequired,
    onSetEditEntry: PropTypes.func.isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = dispatch => ({
    dispatch,
    onClose: () => dispatch(closeExtDeviceManager()),
    onOpenWizard: () => dispatch(openDeviceDeclaration()),
    onSetEditEntry: entry => dispatch(setExtDeviceEditEntry(entry)),
    onSetDeviceData: data => dispatch(setDeviceData(data))
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(ExtDeviceManagerModal);
